import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Typography,
  Row,
  Col,
  Modal,
  Form,
  message,
  Alert,
  Input,
  Statistic,
  List,
  Popconfirm,
  Divider,
  Select,
  Tooltip,
  Badge,
  Space
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  HolderOutlined
} from '@ant-design/icons';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { apiService } from '../../services/api';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

interface ResearchQuestionCustomization {
  customization_id?: string;
  content_category: string;
  customized_text: string;
}

interface ResearchQuestion {
  question_id?: string;
  question_text: string;
  display_order?: string;
  is_active?: boolean;
  customizations: ResearchQuestionCustomization[];
  created_at?: string;
  updated_at?: string;
}

interface StudyInfo {
  spec_id: string;
  project_name: string;
  content_categories: string[];
  status: string;
}

const QuestionManager: React.FC = () => {
  const { studyId } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<ResearchQuestion[]>([]);
  const [studyInfo, setStudyInfo] = useState<StudyInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState<'view' | 'edit' | 'create'>('create');
  const [currentQuestion, setCurrentQuestion] = useState<ResearchQuestion | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (studyId) {
      loadStudyInfo();
      loadQuestions();
    }
  }, [studyId]);

  const loadStudyInfo = async () => {
    try {
      const study = await apiService.getStudy(studyId!);
      setStudyInfo(study);
    } catch (error) {
      console.error('Failed to load study info:', error);
      message.error('Failed to load study information');
    }
  };

  const loadQuestions = async () => {
    setLoading(true);
    try {
      const questionsData = await apiService.getResearchQuestions(studyId!);
      setQuestions(questionsData);
    } catch (error) {
      console.error('Failed to load questions:', error);
      message.error('Failed to load research questions');
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionSave = async (values: any) => {
    setSaving(true);
    try {
      // Calculate the next display order for new questions
      const nextDisplayOrder = currentQuestion?.question_id 
        ? currentQuestion.display_order 
        : (Math.max(...questions.map(q => parseInt(q.display_order || '0')), 0) + 1).toString();

      const questionData = {
        question_text: values.question_text,
        display_order: nextDisplayOrder,
        customizations: values.customizations || []
      };

      if (currentQuestion?.question_id) {
        // Update existing question
        await apiService.updateResearchQuestion(studyId!, currentQuestion.question_id, questionData);
        message.success('Research question updated successfully');
      } else {
        // Create new question
        await apiService.createResearchQuestion(studyId!, questionData);
        message.success('Research question created successfully');
      }

      loadQuestions();
      setModalVisible(false);
      form.resetFields();
      setCurrentQuestion(null);
      setModalMode('create');
    } catch (error: any) {
      console.error('Failed to save research question:', error);
      message.error(error.message || 'Failed to save research question');
    } finally {
      setSaving(false);
    }
  };

  const handleQuestionDelete = async (questionId: string) => {
    try {
      await apiService.deleteResearchQuestion(studyId!, questionId);
      message.success('Research question deleted successfully');
      loadQuestions();
    } catch (error: any) {
      console.error('Failed to delete research question:', error);
      message.error(error.message || 'Failed to delete research question');
    }
  };

  const openQuestionModal = (question?: ResearchQuestion, mode: 'view' | 'edit' | 'create' = 'create') => {
    setCurrentQuestion(question || null);
    setModalMode(mode);
    
    if (question) {
      form.setFieldsValue({
        question_text: question.question_text,
        customizations: question.customizations || []
      });
    } else {
      form.resetFields();
    }
    
    setModalVisible(true);
  };

  const handleToggleActive = async (questionId: string, isActive: boolean) => {
    try {
      await apiService.updateResearchQuestion(studyId!, questionId, { is_active: !isActive });
      message.success(`Question ${!isActive ? 'activated' : 'deactivated'} successfully`);
      loadQuestions();
    } catch (error: any) {
      console.error('Failed to toggle question status:', error);
      message.error(error.message || 'Failed to update question status');
    }
  };

  const handleDragEnd = async (result: any) => {
    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) {
      return;
    }

    // Create a copy of the questions array sorted by display_order
    const sortedQuestions = [...questions].sort((a, b) => 
      parseInt(a.display_order || '0') - parseInt(b.display_order || '0')
    );

    // Reorder the questions array
    const reorderedQuestions = Array.from(sortedQuestions);
    const [removed] = reorderedQuestions.splice(sourceIndex, 1);
    reorderedQuestions.splice(destinationIndex, 0, removed);

    // Update display_order for all questions
    const updatedQuestions = reorderedQuestions.map((question, index) => ({
      ...question,
      display_order: (index + 1).toString()
    }));

    // Optimistically update the UI
    setQuestions(updatedQuestions);

    try {
      // Send reorder request to API
      const reorderData = updatedQuestions.map((question, index) => ({
        question_id: question.question_id!,
        new_order: index + 1
      }));

      await apiService.reorderResearchQuestions(studyId!, reorderData);
      message.success('Questions reordered successfully');
    } catch (error: any) {
      console.error('Failed to reorder questions:', error);
      message.error('Failed to reorder questions');
      // Revert the UI changes
      loadQuestions();
    }
  };



  const activeQuestions = questions.filter(q => q.is_active);
  const totalQuestions = questions.length;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          Research Questions
        </Title>
        <Paragraph>
          Manage research questions for your study. Questions can be customized for different content categories and activated/deactivated as needed.
        </Paragraph>
      </div>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Questions"
              value={totalQuestions}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Questions"
              value={activeQuestions.length}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Inactive Questions"
              value={totalQuestions - activeQuestions.length}
              valueStyle={{ color: '#8c8c8c' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Content Categories"
              value={studyInfo?.content_categories.length || 0}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="Research Questions"
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => openQuestionModal()}
          >
            Add Question
          </Button>
        }
      >
        {!loading && questions.length > 0 && (
          <Alert
            message="Drag and drop questions to reorder them"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            closable
          />
        )}
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="questions">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    Loading questions...
                  </div>
                ) : questions.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <QuestionCircleOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                    <Title level={4} style={{ color: '#999' }}>No Research Questions</Title>
                    <Paragraph style={{ color: '#666' }}>
                      Get started by adding your first research question. Questions will appear here and can be reordered by dragging and dropping.
                    </Paragraph>
                  </div>
                ) : (
                  [...questions]
                    .sort((a, b) => parseInt(a.display_order || '0') - parseInt(b.display_order || '0'))
                    .map((question, index) => (
                      <Draggable key={question.question_id} draggableId={question.question_id!} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            style={{
                              ...provided.draggableProps.style,
                              marginBottom: '8px'
                            }}
                          >
                            <Card
                              size="small"
                              style={{
                                backgroundColor: snapshot.isDragging ? '#f0f0f0' : '#fff',
                                border: snapshot.isDragging ? '2px dashed #1890ff' : '1px solid #d9d9d9'
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div {...provided.dragHandleProps} style={{ cursor: 'move', marginRight: '12px' }}>
                                  <HolderOutlined />
                                </div>
                                <div style={{ flex: 1, minWidth: 0, marginRight: '16px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                                    <Badge 
                                      status={question.is_active ? 'success' : 'default'} 
                                      text={question.is_active ? 'Active' : 'Inactive'}
                                      style={{ marginRight: '12px', flexShrink: 0 }}
                                    />
                                    <Text 
                                      strong 
                                      style={{ 
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        minWidth: 0
                                      }}
                                      title={question.question_text}
                                    >
                                      {question.question_text}
                                    </Text>
                                  </div>
                                  {question.customizations.length > 0 && (
                                    <div>
                                      <Text type="secondary" style={{ fontSize: '12px' }}>
                                        {question.customizations.length} customization(s)
                                      </Text>
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <Space>
                                    <Button
                                      icon={<EyeOutlined />}
                                      onClick={() => openQuestionModal(question, 'view')}
                                      size="small"
                                    >
                                      View
                                    </Button>
                                    <Button
                                      icon={<EditOutlined />}
                                      onClick={() => openQuestionModal(question, 'edit')}
                                      size="small"
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      icon={question.is_active ? <ExclamationCircleOutlined /> : <CheckCircleOutlined />}
                                      onClick={() => handleToggleActive(question.question_id!, question.is_active || false)}
                                      size="small"
                                    >
                                      {question.is_active ? 'Deactivate' : 'Activate'}
                                    </Button>
                                    <Popconfirm
                                      title="Are you sure you want to delete this research question?"
                                      onConfirm={() => handleQuestionDelete(question.question_id!)}
                                      okText="Yes"
                                      cancelText="No"
                                    >
                                      <Button danger icon={<DeleteOutlined />} size="small">
                                        Delete
                                      </Button>
                                    </Popconfirm>
                                  </Space>
                                </div>
                              </div>
                            </Card>
                          </div>
                        )}
                      </Draggable>
                    ))
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Card>

      {/* Question Modal */}
      <Modal
        title={
          modalMode === 'view' ? 'View Research Question' :
          modalMode === 'edit' ? 'Edit Research Question' : 
          'Add Research Question'
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setCurrentQuestion(null);
          setModalMode('create');
        }}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleQuestionSave}
          disabled={saving}
        >
          <Form.Item
            name="question_text"
            label="Global Question Text"
            rules={modalMode !== 'view' ? [{ required: true, message: 'Please enter the research question' }] : []}
          >
            <TextArea
              rows={3}
              placeholder="e.g., Was distant fathers mentioned in the content?"
              disabled={modalMode === 'view'}
            />
          </Form.Item>

          <Divider />

          <Typography.Title level={5}>
            Category Customizations
            <Tooltip title="Customize how this question is asked for different content categories. If no customization is provided, the global question text will be used.">
              <QuestionCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
            </Tooltip>
          </Typography.Title>

          <Form.List name="customizations">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row gutter={[16, 16]} key={key} style={{ marginBottom: 16 }}>
                    <Col span={8}>
                      <Form.Item
                        {...restField}
                        name={[name, 'content_category']}
                        label="Content Category"
                        rules={modalMode !== 'view' ? [{ required: true, message: 'Please select a category' }] : []}
                      >
                        <Select placeholder="Select category" disabled={modalMode === 'view'}>
                          {studyInfo?.content_categories.map((category: string) => (
                            <Select.Option key={category} value={category}>
                              {category}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={14}>
                      <Form.Item
                        {...restField}
                        name={[name, 'customized_text']}
                        label="Customized Question Text"
                        rules={modalMode !== 'view' ? [{ required: true, message: 'Please enter the customized question' }] : []}
                      >
                        <TextArea
                          rows={2}
                          placeholder="e.g., In the film, based on the script, were you able to identify any characters who was a distant father?"
                          disabled={modalMode === 'view'}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={2}>
                      <Form.Item label=" ">
                        <Button
                          type="link"
                          onClick={() => remove(name)}
                          style={{ padding: 0 }}
                          icon={<DeleteOutlined />}
                          disabled={modalMode === 'view'}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                ))}
                {modalMode !== 'view' && (
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      icon={<PlusOutlined />}
                      block
                      disabled={saving}
                    >
                      Add Category Customization
                    </Button>
                  </Form.Item>
                )}
              </>
            )}
          </Form.List>

          {modalMode !== 'view' && (
            <Form.Item>
              <Space>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  loading={saving}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : (currentQuestion ? 'Update Question' : 'Add Question')}
                </Button>
                <Button 
                  onClick={() => setModalVisible(false)}
                  disabled={saving}
                >
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          )}
          
          {modalMode === 'view' && (
            <Form.Item>
              <Button onClick={() => setModalVisible(false)}>
                Close
              </Button>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default QuestionManager; 